/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import { keyframes } from "@emotion/react";
import type { Images } from "@packages/sdk/src/lib/types/schema/imagesSchema";
import { DataTestId } from "@tools/playwright/global";

import { HallowImage, useIsDesktopViewport } from "../../../../../../emotion";
import type { ThemeType } from "../../../../../../global/emotion";
import {
  colors,
  dropShadow,
  numericPixels,
  numericValues,
  radius,
  shades,
} from "../../../../../../global/emotion";
import { VoxIcon } from "../../../../../components/_base/icons/VoxIcon/VoxIcon.emotion";
import type { HallowElementProps } from "../../../../../types/hallowElementProps.types";

const shimmerAnimation = keyframes({
  "0%": {
    opacity: 0,
    transform: "scale(1)",
  },
  "1%": {
    opacity: 0.5,
    transform: "scale(1)",
  },
  "40%": {
    opacity: 0,
    transform: "scale(1.3)",
  },
  "100%": {
    opacity: 0,
    transform: "scale(1)",
  },
});

const nonAnimatedImageStyle: Interpolation = {
  borderRadius: radius.m,
  boxShadow: `${numericValues[0]} ${dropShadow.ms} ${dropShadow.m} ${numericValues[0]} ${shades.shade20}`,
  opacity: 1,
  transition: "all 300ms ease-out",
  willChange: "width, height, border-radius, opacity",
};
const animatedImageStyle: Interpolation = {
  animationDelay: "8s",
  animationDuration: "2s",
  animationIterationCount: "infinite",
  animationName: shimmerAnimation,
  borderRadius: radius.m,
  left: 0,
  opacity: 0,
  position: "absolute",
  willChange: "opacity, transform",
};
const buttonStyle: Interpolation = {
  WebkitTapHighlightColor: colors.transparent,
  background: colors.transparent,
  border: 0,
  cursor: "pointer",
  ":disabled": { cursor: "default" },
  lineHeight: numericValues[0],
  pointerEvents: "auto",
  position: "relative",
  transition: "opacity 300ms ease-out",
  willChange: "opacity",
  padding: 0,
};
const isHeaderCollapsedImageStyle: Interpolation = {
  borderRadius: radius.s,
  height: numericPixels[40],
  width: numericPixels[40],
};
const iconStyle: Interpolation = {
  display: "flex",
  height: numericPixels[40],
  left: "50%",
  placeContent: "center",
  placeItems: "center",
  position: "absolute",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: numericPixels[40],
  willChange: "width, height, opacity",
  zIndex: 1,
};
const buttonIconStyle: Interpolation = {
  backgroundImage:
    'url("data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4NCjxnIGZpbHRlcj0idXJsKCNmaWx0ZXIwX2JfMTQzM18xNjQ4KSI+DQo8cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSIyMCIgZmlsbD0iYmxhY2siIGZpbGwtb3BhY2l0eT0iMC4zIi8+DQo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTE2Ljg5NDQgMTQuMjExMUMxNi4yNzQ1IDEzLjkwMTIgMTUuNTM4MiAxMy45MzQzIDE0Ljk0ODUgMTQuMjk4N0MxNC4zNTg5IDE0LjY2MzEgMTQgMTUuMzA2OCAxNCAxNlYyNEMxNCAyNC42OTMyIDE0LjM1ODkgMjUuMzM2OSAxNC45NDg1IDI1LjcwMTNDMTUuNTM4MiAyNi4wNjU3IDE2LjI3NDUgMjYuMDk4OCAxNi44OTQ0IDI1Ljc4ODlMMjQuODk0NCAyMS43ODg5QzI1LjU3MiAyMS40NTAxIDI2IDIwLjc1NzUgMjYgMjBDMjYgMTkuMjQyNSAyNS41NzIgMTguNTQ5OSAyNC44OTQ0IDE4LjIxMTFMMTYuODk0NCAxNC4yMTExWiIgZmlsbD0id2hpdGUiLz4NCjwvZz4NCjxkZWZzPg0KPGZpbHRlciBpZD0iZmlsdGVyMF9iXzE0MzNfMTY0OCIgeD0iLTQwIiB5PSItNDAiIHdpZHRoPSIxMjAiIGhlaWdodD0iMTIwIiBmaWx0ZXJVbml0cz0idXNlclNwYWNlT25Vc2UiIGNvbG9yLWludGVycG9sYXRpb24tZmlsdGVycz0ic1JHQiI+DQo8ZmVGbG9vZCBmbG9vZC1vcGFjaXR5PSIwIiByZXN1bHQ9IkJhY2tncm91bmRJbWFnZUZpeCIvPg0KPGZlR2F1c3NpYW5CbHVyIGluPSJCYWNrZ3JvdW5kSW1hZ2VGaXgiIHN0ZERldmlhdGlvbj0iMjAiLz4NCjxmZUNvbXBvc2l0ZSBpbjI9IlNvdXJjZUFscGhhIiBvcGVyYXRvcj0iaW4iIHJlc3VsdD0iZWZmZWN0MV9iYWNrZ3JvdW5kQmx1cl8xNDMzXzE2NDgiLz4NCjxmZUJsZW5kIG1vZGU9Im5vcm1hbCIgaW49IlNvdXJjZUdyYXBoaWMiIGluMj0iZWZmZWN0MV9iYWNrZ3JvdW5kQmx1cl8xNDMzXzE2NDgiIHJlc3VsdD0ic2hhcGUiLz4NCjwvZmlsdGVyPg0KPC9kZWZzPg0KPC9zdmc+")',
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  backgroundSize: "contain",
};
const isHeaderCollapsedIconStyle: Interpolation = {
  height: numericPixels[20],
  width: numericPixels[20],
};
const activatedContainerStyle: Interpolation = {
  display: "flex",
  lineHeight: numericValues[0],
  placeContent: "center",
  placeItems: "center",
  position: "relative",
};
const hallowImageStyle: Interpolation<ThemeType> = (theme) => ({
  backgroundColor: theme.semanticColors.neutralsMedium,
  flexShrink: 0,
  objectFit: "cover",
  objectPosition: "center",
  objectRepeat: "no-repeat",
});

export type ReaderThumbnailProps = HallowElementProps<"button"> & {
  activated: boolean;
  imageSrc: Images;
  playing: boolean;
  isHeaderCollapsed: boolean;
  title: string;
};

export const ReaderThumbnail = ({
  isHeaderCollapsed,
  imageSrc,
  playing,
  activated,
  title,
  ...buttonProps
}: ReaderThumbnailProps) => {
  const isDesktop = useIsDesktopViewport();
  const img = (
    <>
      {!activated && !isHeaderCollapsed ? (
        <HallowImage
          src={imageSrc as Images}
          size={"s"}
          alt={title}
          width={isDesktop ? 80 : 72}
          height={isDesktop ? 80 : 72}
          css={[hallowImageStyle, animatedImageStyle]}
        />
      ) : null}
      <HallowImage
        src={imageSrc as Images}
        size={"s"}
        alt={title}
        width={isDesktop ? 80 : 72}
        height={isDesktop ? 80 : 72}
        css={[
          hallowImageStyle,
          nonAnimatedImageStyle,
          isHeaderCollapsed ? isHeaderCollapsedImageStyle : null,
        ]}
      />
    </>
  );

  if (activated) {
    return (
      <div css={activatedContainerStyle} className={buttonProps.className}>
        {img}
        <VoxIcon playing={playing} css={iconStyle} />
      </div>
    );
  }
  return (
    <button
      css={buttonStyle}
      {...buttonProps}
      data-testid={DataTestId.readerThumbnail}
    >
      {img}
      <span
        css={[
          iconStyle,
          buttonIconStyle,
          isHeaderCollapsed ? isHeaderCollapsedIconStyle : null,
        ]}
      />
    </button>
  );
};
